import { Tabs, message } from 'antd';
import { HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { StoreContext } from 'views/Conversation/Sider/useInvoice';
import styled from '@emotion/styled';
import useTrade from 'views/Conversation/Sider/useTrade';
import { eventBus } from 'services/eventBus';
import Trade from 'views/Conversation/Sider/Trade';
import Product from 'views/Conversation/Sider/Product';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { ToDoBundleProvider } from 'views/Conversation/Sider/ToDoBundle/context';
import Detail from 'views/Conversation/Sider/ToDoBundle/Detail';
import { noop, uniq } from 'lodash-es';
import { TodoContextProvider } from 'views/Aide/Basis/Todo/Context';
import { useSetAtom } from 'jotai';
import { todoCurrentId } from 'views/Conversation/Sider/ToDoBundle/state';
import { TypeSourceProvider } from '../../Conversation/Sider/ToDoBundle/TypeSourceContext';
import Voucher from 'views/Conversation/Sider/Voucher';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import {
  usePhraseSideModeValue,
  useSetPhraseSideMode,
} from 'views/Conversation/Chat/components/Phrase/state';
import { OrderCard } from 'components/LightConversation/components/OrderCard';
import { LightChatSideContext } from 'components/LightConversation/contexts/LightChatSide';
import { useDetail } from 'views/Conversation/Sider/ToDoBundle/useApi';
import { TicketDetail } from 'components/LightConversation/components/Ticket/TicketDetail';
import { useTicketTask } from 'hooks/business/chat/useTicket';
import { useLatest } from 'ahooks';

export interface TodoSiderProps extends HTMLAttributes<HTMLElement> {
  conversation: Conversation.Conversation;
  todo: Todo.Record;
  onUpdate?: () => void;
  readonly?: boolean;
  isLightChatLayout: boolean;
}

const LightTodoContent = (props: {
  storeId: string;
  conversation: Conversation.Conversation;
  onUpdate?: (todoDetail?: Todo.TodoInstance) => void;
}) => {
  const { storeId, conversation, onUpdate } = props;
  const { detail } = useDetail();
  const { task, policies, typeSource } = useTicketTask(storeId);

  const ref = useLatest(onUpdate);

  useEffect(() => {
    ref?.current?.(detail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  if (!detail || !task) {
    return null;
  }

  const { conversationId, channel, externalBuyerId } = conversation;

  return (
    <TicketDetail
      ticket={detail}
      task={task}
      policies={policies}
      typeSource={typeSource}
      listParams={null}
      conversationId={conversationId}
      platform={channel as General.PlatformUpperCase}
      externalBuyerId={externalBuyerId}
      cachedScrollTop={0}
      onSaveScrollTop={noop}
      onUpdate={() => {
        eventBus.emit('TODO_UPDATE', detail.id);
      }}
      deleteEnabled={false}
      transferEnabled={false}
    />
  );
};

function TodoSider(props: TodoSiderProps) {
  const {
    conversation,
    todo,
    onUpdate,
    readonly = false,
    isLightChatLayout,
    ...otherProps
  } = props;

  const { conversationId, channel, buyerNick, buyerId, storeId, storeName, tunnelAccountId } =
    conversation;

  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslate();

  const [activeKey, setActiveKey] = useState('todo');

  const tradeRef = useRef<any>();

  useEffect(() => {
    const handler = (trade: Conversation.Trade.TradeWithLogistics) => {
      setActiveKey('trade');
      requestAnimationFrame(() => {
        tradeRef?.current?.openDetail?.(trade);
      });
    };
    eventBus.on('CONV_OPEN_SIDE_ORDER_DETAIL', handler);
    return () => {
      eventBus.off('CONV_OPEN_SIDE_ORDER_DETAIL', handler);
    };
  }, []);

  const setTodoCurrentId = useSetAtom(todoCurrentId);

  useEffect(() => {
    const handler = () => {
      setActiveKey('todo');
    };
    eventBus.on('RELATED_MSGID_OPEN_TODO', handler);
    return () => {
      eventBus.off('RELATED_MSGID_OPEN_TODO', handler);
    };
  }, []);

  const [todoDetail, setTodoDetail] = useState<Todo.TodoInstance>();

  const {
    list: [relatedTrade] = [],
    loading: tradeLoading,
    fetchList,
    setList,
  } = useTrade(storeId);

  useEffect(() => {
    setTodoCurrentId(todo.id);

    return () => {
      setTodoDetail(undefined);
      setTodoCurrentId('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todo.id]);

  useEffect(() => {
    if (todoDetail?.relatedTradeId) {
      fetchList(
        {
          page_no: 1,
          page_size: 1,
          internal_buyer_id: buyerId,
          internal_store_id: storeId,
          tunnelAccountId,
          external_trade_ids: todoDetail.relatedTradeId,
        },
        channel,
      ).catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      });
    }

    return () => setList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todoDetail?.relatedTradeId, buyerId, storeId, tunnelAccountId, channel]);

  const todoNode = (
    <ToDoBundleProvider
      value={{
        conversationId,
        storeId,
        buyerId,
        platform: channel as any,
        externalBuyerId: conversation.externalBuyerId,
        assistantId: conversation.assistantId,
        region: conversation.region,
        conversation,
      }}
    >
      <TypeSourceProvider>
        {isLightChatLayout ? (
          <LightTodoContent
            storeId={storeId}
            conversation={conversation}
            onUpdate={(todoDetail) => {
              setTodoDetail(todoDetail);
              onUpdate?.();
            }}
          />
        ) : (
          <Detail
            readonly={readonly}
            onUpdate={(todoDetail) => {
              setTodoDetail(todoDetail);
              onUpdate?.();
            }}
          />
        )}
      </TypeSourceProvider>
    </ToDoBundleProvider>
  );

  const tradeNode = todoDetail ? (
    isLightChatLayout ? (
      <LightChatSideContext.Provider value={{ containerRef: containerRef }}>
        <div>
          <Related>{t('domain.task.field.relatedOrder')}</Related>
          <OrderCard sendable={false} conversation={conversation} order={relatedTrade} />
        </div>
      </LightChatSideContext.Provider>
    ) : (
      <Trade
        key={conversationId}
        ref={tradeRef}
        showFilter={false}
        conversation={conversation}
        drawerWidth={504}
        relatedTrade={relatedTrade}
        relatedRender={(node: ReactNode) => [
          <Related>{t('domain.task.field.relatedOrder')}</Related>,
          node,
        ]}
      />
    )
  ) : null;

  const { phraseModal } = useChatConversationContext();
  const phraseKey = 'pharse';
  const closePhraseKey = 'closePhrase';

  const phraseSideMode = usePhraseSideModeValue();
  const setPhraseSideMode = useSetPhraseSideMode();

  const tabs = (
    <Tabs
      activeKey={phraseModal?.visible ? phraseKey : activeKey}
      onChange={(key) => {
        if (key === phraseKey) {
          phraseModal?.show(phraseModal?.meta);
        } else {
          phraseModal?.hide();
          if (key === closePhraseKey) {
            setPhraseSideMode(false);
          } else {
            setActiveKey(key);
          }
        }
      }}
    >
      <Tabs.TabPane key="todo" tab={t('scene.chatWorkspace.taskTab.title.processingProgress')}>
        {todoNode}
      </Tabs.TabPane>
      <Tabs.TabPane key="trade" tab={t('tab.trade')}>
        {tradeNode}
      </Tabs.TabPane>
      {isLightChatLayout ? null : (
        <>
          {channel !== 'MERCADO' ? (
            <Tabs.TabPane key="product" tab={t('tab.product')}>
              {tradeLoading ? (
                <LoadingOutlined />
              ) : (
                <Product
                  key={conversationId}
                  showFilter={false}
                  conversation={conversation}
                  relatedId={
                    relatedTrade
                      ? uniq(relatedTrade.items.map(({ itemId }) => itemId)).join(',')
                      : todoDetail?.relatedItemId
                  }
                  relatedRender={(node: ReactNode) => [
                    <Related>{t('domain.task.field.relatedProduct')}</Related>,
                    node,
                  ]}
                />
              )}
            </Tabs.TabPane>
          ) : null}
          <Tabs.TabPane key="voucher" tab={t('tab.voucher')}>
            <Voucher
              style={{
                '--voucher-drawer-width': '504px',
              }}
              key={conversationId}
              conversation={conversation}
            />
          </Tabs.TabPane>
          {phraseSideMode && (
            <Tabs.TabPane key={phraseKey} tab={t('phrase.快捷回复')}>
              {phraseModal?.meta}
            </Tabs.TabPane>
          )}
          {phraseSideMode && (
            <Tabs.TabPane
              key={closePhraseKey}
              tab={<CloseOutlined style={{ margin: '-12px' }} />}
            />
          )}
        </>
      )}
    </Tabs>
  );

  return (
    <StoreContext.Provider
      value={{
        buyerNick,
        storeId,
        storeName,
      }}
    >
      <PreviewModalProvider>
        <TodoContextProvider>
          <Aside {...otherProps} ref={containerRef}>
            {tabs}
          </Aside>
        </TodoContextProvider>
      </PreviewModalProvider>
    </StoreContext.Provider>
  );
}

const Aside = styled.aside`
  position: relative;
  > .ant-tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
    .ant-tabs-nav {
      flex: 0 0 auto;
      margin-bottom: 0;
      .ant-tabs-nav-wrap {
        padding: 0 12px;
      }
    }
    .ant-tabs-content-holder {
      flex: 1 0 0;
      background: var(--fett-bg-subtle);
      .ant-tabs-content {
        height: 100%;
        .ant-tabs-tabpane > div {
          height: 100%;
        }
      }
    }
  }
`;

const Related = styled.div`
  height: 30px;
  line-height: 30px;
  text-align: center;
  color: var(--ant-primary-color);
  background: var(--ant-primary-1);
`;

export default TodoSider;
